import {
    MT_ENTITY_LIST_API,
    MT_PREPARE_PERIOD_OPTIONS_API,
} from '@/multi-territory/mt-services/mt-api-endpoints'
import {
    mtBuildRequestHeader,
    mtUrlBuilder,
} from '@/multi-territory/mt-services/mt-api-utils'
import {
    fetchMTEntityFailure,
    fetchMTEntityStart,
    fetchMTEntitySuccess,
    fetchMTPeriodOptionsFailure,
    fetchMTPeriodOptionsStart,
    fetchMTPeriodOptionsSuccess,
    fetchMTTanFailure,
    fetchMTTanStart,
    fetchMTTanSuccess,
    setMTAutoSelectedUIN,
} from '../slice/mtEntityTanSlice'

/**
 * Fetch MT Entity list (POST /wht-mt-admin/api/v1/entity-master/list/{countryCode})
 * Caches in Redux - won't re-fetch if already loaded
 */
export const fetchMTEntityListIfNeeded =
  countryCode => async (dispatch, getState) => {
    const { entityList, entityListCountryCode } = getState().mtEntityTan
    if (
      entityList &&
      entityList.length > 0 &&
      entityListCountryCode === countryCode
    )
      return

    try {
      dispatch(fetchMTEntityStart())
      const url = mtUrlBuilder(MT_ENTITY_LIST_API(countryCode))
      const payload = {
        pageFilter: {
          currentPage: 1,
          includeCount: true,
          pageSize: 500000,
          sortField: 'entityName',
          sortOrder: 'ASC',
        },
        requestFilter: [],
        isReportDownload: false,
        searchCOList: [],
      }
      const options = mtBuildRequestHeader('POST', payload)
      const response = await fetch(url, options)
      if (!response.ok) throw new Error('Failed to fetch entity list')
      const json = await response.json()
      const content = json?.data?.content || []
      dispatch(fetchMTEntitySuccess({ content, countryCode }))
    } catch (error) {
      dispatch(
        fetchMTEntityFailure(error.message || 'Failed to fetch entities')
      )
    }
  }

/**
 * Fetch TAN records by entity UUID
 * GET /wht-mt-admin/api/v1/tan-master/by/entityUuid/{entityUuid}?countryCode={countryCode}
 * Caches per entityUuid
 *
 * Response shape:
 * { data: { tanApplicable, uniqueIdentificationNumber, entityName, tanRecords: [...] }, success }
 *
 * If tanApplicable=false, no TAN dropdown is shown and UIN is auto-selected.
 */
export const fetchMTTanByEntityIfNeeded =
  (entityUuid, countryCode) => async (dispatch, getState) => {
    const { tanListByEntity } = getState().mtEntityTan
    if (tanListByEntity[entityUuid]) return // Already cached

    try {
      dispatch(fetchMTTanStart())
      const endpoint =
        'wht-mt-admin/api/v1/tan-master/by/entityUuid/' + entityUuid
      const url = mtUrlBuilder(endpoint, {}, { countryCode })
      const options = mtBuildRequestHeader('GET')
      const response = await fetch(url, options)
      if (!response.ok) throw new Error('Failed to fetch TAN list')
      const json = await response.json()
      const data = json?.data || {}
      const tanRecords = data.tanRecords || []
      const entityMeta = {
        tanApplicable: data.tanApplicable ?? true,
        uniqueIdentificationNumber: data.uniqueIdentificationNumber || '',
        entityName: data.entityName || '',
        entityMasterUuid: data.entityMasterUuid || entityUuid,
      }
      dispatch(
        fetchMTTanSuccess({ entityUuid, tanList: tanRecords, entityMeta })
      )

      // If TAN is not applicable, auto-select the UIN as identifier
      if (!entityMeta.tanApplicable && entityMeta.uniqueIdentificationNumber) {
        dispatch(setMTAutoSelectedUIN(entityMeta.uniqueIdentificationNumber))
      }
    } catch (error) {
      dispatch(fetchMTTanFailure(error.message || 'Failed to fetch TAN'))
    }
  }

/**
 * Fetch period options (years + months) from API
 * GET /wht-mt-data-management/api/v1/prepare/period-options
 * Header: X-Country-Code
 * Caches in Redux - won't re-fetch if already loaded for the same country.
 *
 * Pass `forceRefresh = true` to bypass the cache and always hit the API
 * (e.g. after a data upload introduces a new year/period).
 */
export const fetchMTPeriodOptionsIfNeeded =
  (countryCode, forceRefresh = false) =>
  async (dispatch, getState) => {
    const { periodOptions, periodOptionsCountryCode } = getState().mtEntityTan
    if (
      !forceRefresh &&
      periodOptions &&
      periodOptionsCountryCode === countryCode
    )
      return

    try {
      dispatch(fetchMTPeriodOptionsStart())
      const url = mtUrlBuilder(MT_PREPARE_PERIOD_OPTIONS_API)
      const options = mtBuildRequestHeader('GET', null, {
        'X-Country-Code': countryCode,
      })
      const response = await fetch(url, options)
      if (!response.ok) throw new Error('Failed to fetch period options')
      const json = await response.json()
      dispatch(
        fetchMTPeriodOptionsSuccess({ data: json?.data || json, countryCode })
      )
    } catch (error) {
      dispatch(
        fetchMTPeriodOptionsFailure(
          error.message || 'Failed to fetch period options'
        )
      )
    }
  }
